import Modal from 'react-bootstrap/Modal'
import ModalBody from 'react-bootstrap/ModalBody'
import { faFull, faPlain } from 'utilities/helper/format'

/**
 * The (i) behind امتیاز عضو — the score's own arithmetic, with THIS member's
 * numbers in it.
 *
 * A score a manager cannot check is a score they will not trust, and this one
 * ranks their neighbours. So the modal is not a definition of the metric; it
 * is the member's three terms, in the fund's own units, adding up to the
 * number on the row.
 *
 * «تومان-روز» is spelled out rather than hidden behind a friendlier word,
 * because the whole idea — money times the days it stayed — is the thing the
 * manager has to agree with for the ranking to mean anything.
 */

/** Toman-days are astronomically large; «۱۲ میلیون تومان-روز» is readable. */
const tomanDays = (value: number) => {
    const magnitude = Math.abs(value)
    if (magnitude >= 1_000_000_000)
        return `${faFull.format(Math.round(value / 100_000_000) / 10)} میلیارد`
    if (magnitude >= 1_000_000)
        return `${faFull.format(Math.round(value / 100_000) / 10)} میلیون`
    if (magnitude >= 1_000) return `${faFull.format(Math.round(value / 1_000))} هزار`
    return faFull.format(Math.round(value))
}

function Term({
    sign,
    label,
    value,
    detail,
}: {
    sign: '+' | '−'
    label: string
    value: number
    detail: string
}) {
    if (value === 0 && sign === '−') return null
    return (
        <div className=" flex items-start justify-between gap-3 border-b border-divider py-2 last:border-b-0">
            <div className=" text-right">
                <p className=" text-xs font-bold text-text-body">
                    <span
                        aria-hidden
                        className={` ml-1 inline-block ${
                            sign === '+' ? ' text-brand' : ' text-danger'
                        }`}
                    >
                        {sign}
                    </span>
                    {label}
                </p>
                <p className=" mt-0.5 text-[11px] leading-5 text-text-secondary">
                    {detail}
                </p>
            </div>
            <span
                className={` shrink-0 whitespace-nowrap text-xs font-bold tabular-nums ${
                    sign === '+' ? ' text-brand-deep' : ' text-danger-on'
                }`}
            >
                {tomanDays(value)}
            </span>
        </div>
    )
}

export default function ScoreExplainer({
    show,
    onHide,
    candidate,
    overdueMultiplier,
    membersCount,
    inflationApplied,
}: {
    show: boolean
    onHide: () => void
    candidate: ILoanCandidate | null
    overdueMultiplier: number
    membersCount: number
    inflationApplied: boolean
}) {
    if (!candidate) return null
    const { breakdown } = candidate

    return (
        // `scrollable` alone did nothing: it only styles `.modal-body`, and
        // the whole explainer used to sit in a plain <div>. A member with a
        // debt line and a late-instalment term makes this taller than a
        // phone screen, so the modal ran off both ends with «فهمیدم»
        // somewhere below the fold and no way to scroll to it — the body has
        // to be a real Modal.Body, and the button has to live outside it.
        <Modal
            show={show}
            onHide={onHide}
            size="sm"
            centered
            scrollable
            aria-labelledby="score-explainer-title"
        >
            <ModalBody className=" px-4 py-4">
                <p
                    id="score-explainer-title"
                    className=" text-center text-sm font-bold text-heading"
                >
                    امتیاز {candidate.member_name} چطور حساب شده؟
                </p>
                <p className=" mt-2 rounded-xl bg-brand-soft px-3 py-2 text-center text-[11px] leading-6 text-brand-deep">
                    واحد امتیاز «تومان-روز» است: هر تومانی که عضو در صندوق
                    گذاشته، ضربدر تعداد روزهایی که آنجا مانده. یعنی هم مبلغ
                    مهم است، هم مدت.
                </p>

                <div className=" mt-3">
                    <Term
                        sign="+"
                        label="سرمایه‌ای که در صندوق گذاشته"
                        value={breakdown.supply}
                        detail="واریزها منهای برداشت‌ها، هرکدام ضربدر روزهایی که پول در صندوق مانده. بازپرداخت قسط حساب نمی‌شود؛ برگرداندن پول قرض‌گرفته، تامین صندوق نیست."
                    />
                    <Term
                        sign="−"
                        label="وامی که خودش در دست داشته"
                        value={breakdown.borrow}
                        detail="امتیاز بابت توانی است که به صندوق داده تا به دیگران وام بدهد، نه پولی که خودش برداشته."
                    />
                    <Term
                        sign="−"
                        label={`دیرکرد در پرداخت اقساط (${faPlain.format(
                            overdueMultiplier
                        )} برابر)`}
                        value={breakdown.late_penalty}
                        detail={`مبلغ هر قسط ضربدر روزهای تاخیرش — هم قسط‌های معوق فعلی، هم قسط‌هایی که بالاخره پرداخت شدند ولی دیر. هر روز تاخیر ${faPlain.format(
                            overdueMultiplier
                        )} برابرِ همان مبلغ-روزِ سرمایه اثر منفی دارد.`}
                    />
                </div>

                <div className=" mt-3 rounded-xl bg-canvas p-3">
                    <div className=" flex items-center justify-between">
                        <span className=" text-xs text-text-secondary">
                            امتیاز خام
                        </span>
                        <span className=" text-xs font-bold tabular-nums text-text-body">
                            {tomanDays(candidate.raw_score)} تومان-روز
                        </span>
                    </div>
                    <div className=" mt-1.5 flex items-center justify-between">
                        <span className=" text-xs text-text-secondary">
                            نسبت به بیشترین امتیاز صندوق
                        </span>
                        <span className=" text-xs font-bold tabular-nums text-brand-deep">
                            {faPlain.format(candidate.score)} از ۱۰۰
                        </span>
                    </div>
                    <div className=" mt-1.5 flex items-center justify-between">
                        <span className=" text-xs text-text-secondary">
                            جایگاه در صندوق
                        </span>
                        <span className=" text-xs font-bold tabular-nums text-brand-deep">
                            رتبهٔ {faPlain.format(candidate.rank)} از{' '}
                            {faPlain.format(membersCount)}
                        </span>
                    </div>
                    {/* both numbers are shown because either alone misleads:
                        contribution is heavy-tailed, so one large member can
                        push everyone else's ratio near zero even when their
                        ranking is meaningful */}
                    <p className=" mt-1.5 text-[11px] leading-5 text-text-secondary">
                        نوار روی فهرست، جایگاه است نه نسبت — چون در بیشتر
                        صندوق‌ها یکی‌دو عضو خیلی بزرگ‌تر از بقیه‌اند و اگر
                        نسبت را نشان می‌دادیم، نوار بقیه تقریباً خالی می‌ماند.
                    </p>
                </div>

                <p className=" mt-3 text-[11px] leading-6 text-text-secondary">
                    {inflationApplied
                        ? 'تعدیل تورمی روشن است: مبلغ‌های قدیمی‌تر با شاخص قیمت مصرف‌کننده به پول امروز تبدیل شده‌اند، چون ۱ میلیون تومان چند سال پیش بیشتر از ۱ میلیون امروز بوده. این تبدیل تقریبی است.'
                        : 'می‌توانید «تعدیل تورمی» را روشن کنید تا مبلغ‌های قدیمی‌تر با شاخص تورم به پول امروز تبدیل شوند.'}
                </p>
                <p className=" mt-2 text-[11px] leading-6 text-text-secondary">
                    امتیاز فقط برای مرتب‌کردن فهرست است و جلوی اعطای وام به
                    هیچ‌کس را نمی‌گیرد. تصمیم با شماست.
                </p>

            </ModalBody>

            {/* Outside the scroll area on purpose: the way out of a modal
                must never be something you have to scroll to find. */}
            <div className=" shrink-0 px-4 pb-4 pt-2">
                <button
                    type="button"
                    onClick={onHide}
                    className=" w-full rounded-full bg-green-1 py-2.5 text-[15px] font-bold text-white active:scale-[0.99] transition-transform motion-reduce:transition-none"
                >
                    فهمیدم
                </button>
            </div>
        </Modal>
    )
}
